export const BETA_ACCESS_CACHE_TTL_MS = 15 * 60 * 1000;

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

type CachedEnvelope<TValue> = {
  cachedAt: number;
  value: TValue;
};

const getCacheKey = (userId: string) => `beta-access:${userId}`;

export const readCachedValue = <TValue>(
  storage: StorageLike | null | undefined,
  userId: string,
  now = Date.now()
): TValue | null => {
  if (!storage) return null;

  const raw = storage.getItem(getCacheKey(userId));
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as CachedEnvelope<TValue>;
    if (!parsed || typeof parsed.cachedAt !== "number") {
      storage.removeItem(getCacheKey(userId));
      return null;
    }

    if (now - parsed.cachedAt > BETA_ACCESS_CACHE_TTL_MS) {
      storage.removeItem(getCacheKey(userId));
      return null;
    }

    return parsed.value ?? null;
  } catch {
    storage.removeItem(getCacheKey(userId));
    return null;
  }
};

export const writeCachedValue = <TValue>(
  storage: StorageLike | null | undefined,
  userId: string,
  value: TValue,
  now = Date.now()
) => {
  if (!storage) return;

  const envelope: CachedEnvelope<TValue> = {
    cachedAt: now,
    value,
  };

  storage.setItem(getCacheKey(userId), JSON.stringify(envelope));
};

export const clearCachedValue = (
  storage: StorageLike | null | undefined,
  userId: string
) => {
  if (!storage) return;
  storage.removeItem(getCacheKey(userId));
};
